import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, CreditCard, ArrowRight, ArrowLeft, Loader2, Sparkles, Mail } from "lucide-react";

const CYCLES = [
  { value: "monthly", label: "Monatlich" },
  { value: "yearly", label: "Jährlich" },
];

function formatEur(v) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
}

function savingsPct(plan) {
  if (!plan?.yearly_price || !plan?.monthly_price) return 0;
  return Math.round((1 - plan.yearly_price / (plan.monthly_price * 12)) * 100);
}

function PlanCard({ plan, cycle, selected, onSelect }) {
  const isYearly = cycle === "yearly";
  const shownMonthly = isYearly ? Math.round(plan.yearly_price / 12) : plan.monthly_price;
  return (
    <button
      type="button"
      data-testid={`plan-card-${plan.plan_name.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={() => onSelect(plan.plan_name)}
      className={`text-left rounded-xl border p-5 transition-all relative ${
        selected
          ? "border-orange-500 bg-orange-50/60 ring-2 ring-orange-500"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      {plan.recommended && (
        <Badge className="absolute -top-2 right-3 bg-orange-600 hover:bg-orange-600 text-white text-[10px]">
          Empfohlen
        </Badge>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-bold text-slate-900">{plan.plan_name.replace(/^Client /, "")}</p>
          <p className="text-xs text-slate-500">bis {plan.monthly_lead_limit.toLocaleString("de-DE")} Leads / Monat</p>
        </div>
        {selected && <Check className="w-5 h-5 text-orange-600 shrink-0" />}
      </div>
      <div className="mb-3">
        <span className="text-2xl font-extrabold text-slate-900">{formatEur(shownMonthly)}</span>
        <span className="text-sm text-slate-500"> /Monat</span>
        {isYearly && (
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`plan-yearly-note-${plan.plan_name.toLowerCase().replace(/\s+/g, "-")}`}>
            {formatEur(plan.yearly_price)} jährlich abgerechnet
          </p>
        )}
      </div>
      <ul className="space-y-1.5">
        {plan.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
            <Check className="w-3.5 h-3.5 text-orange-600 mt-0.5 shrink-0" /> <span>{f}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}

export default function ClientPurchaseWizard({ open, onOpenChange, onStarted }) {
  const [step, setStep] = useState(1); // 1=Plan, 2=Form, 3=Submitting
  const [catalog, setCatalog] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [planName, setPlanName] = useState("");
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [pricingMode, setPricingMode] = useState("direct"); // "direct" | "wholesale"
  const [volumeHint, setVolumeHint] = useState({ active_count: 0, show_custom_deal_cta: false, message: "" });
  const [adminModeOverride, setAdminModeOverride] = useState(""); // platform_admin only

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingCatalog(true);
        const url = adminModeOverride ? `/agency/client-subscription/catalog?mode=${adminModeOverride}` : "/agency/client-subscription/catalog";
        const r = await api.get(url);
        if (cancelled) return;
        setCatalog(r.data.catalog || []);
        setPricingMode(r.data.pricing_mode || "direct");
        setVolumeHint(r.data.volume_hint || { active_count: 0, show_custom_deal_cta: false, message: "" });
        // Default-Plan = empfohlen oder erster
        const recommended = (r.data.catalog || []).find((p) => p.recommended) || (r.data.catalog || [])[0];
        if (recommended) setPlanName(recommended.plan_name);
      } catch (e) {
        toast.error("Pakete konnten nicht geladen werden");
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, adminModeOverride]);

  // Reset bei jedem Öffnen — deferred (vermeidet R19 set-state-in-effect)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setStep(1);
      setForm({ company_name: "", contact_person: "", email: "", phone: "" });
    });
    return () => { cancelled = true; };
  }, [open]);

  const selectedPlan = useMemo(() => catalog.find((p) => p.plan_name === planName), [catalog, planName]);

  const validForm = form.company_name.trim() && form.email.trim();

  const goNext = () => {
    if (step === 1 && !planName) {
      toast.error("Bitte ein Paket auswählen");
      return;
    }
    setStep(2);
  };

  const goBack = () => {
    if (step === 2) setStep(1);
  };

  const onSubmit = async () => {
    if (!validForm) {
      toast.error("Bitte Pflichtfelder ausfüllen (Firmenname, E-Mail)");
      return;
    }
    setSubmitting(true);
    try {
      const url = adminModeOverride ? `/agency/client-checkout/start?mode=${adminModeOverride}` : "/agency/client-checkout/start";
      const r = await api.post(url, {
        company_name: form.company_name.trim(),
        contact_person: form.contact_person.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        plan_name: planName,
        billing_cycle: cycle,
      });
      const checkoutUrl = r.data.checkout_url;
      const pcid = r.data.pending_checkout_id;
      if (onStarted) onStarted({ pcid });
      if (checkoutUrl) {
        // Hard redirect zur Stripe-Hosted-Checkout-Page
        window.location.href = checkoutUrl;
        return;
      }
      // Falls Stripe nicht konfiguriert ist: Hinweis statt Redirect
      toast.success("Pending-Checkout angelegt – Stripe ist noch nicht konfiguriert");
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Checkout konnte nicht gestartet werden";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent
        data-testid="client-purchase-wizard"
        className="max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle>Neuen Mandanten kaufen</DialogTitle>
          <DialogDescription>
            Wählen Sie ein Paket und schließen Sie die Bezahlung über Stripe ab. Der Mandant wird erst nach erfolgreicher Zahlung angelegt.
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <span className={`inline-flex items-center gap-1 ${step === 1 ? "text-orange-600 font-semibold" : ""}`}>
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${step === 1 ? "bg-orange-600 text-white" : "bg-slate-200"}`}>1</span> Paket
          </span>
          <span>—</span>
          <span className={`inline-flex items-center gap-1 ${step === 2 ? "text-orange-600 font-semibold" : ""}`}>
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${step === 2 ? "bg-orange-600 text-white" : "bg-slate-200"}`}>2</span> Kunde
          </span>
          <span>—</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] bg-slate-200">3</span> Bezahlen
          </span>
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-plan">
            {/* Wholesale-Badge */}
            {pricingMode === "wholesale" && (
              <div className="flex items-center justify-center" data-testid="wholesale-badge">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 border border-orange-300 text-orange-900 text-xs font-bold uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" /> Agentur-Wholesale — exklusiv für Agentur-Partner
                </span>
              </div>
            )}
            {/* Platform-Admin-Mode-Switch */}
            <PlatformAdminModeSwitch
              modeOverride={adminModeOverride}
              setModeOverride={setAdminModeOverride}
              currentMode={pricingMode}
            />
            {/* Cycle Toggle */}
            <div className="flex items-center justify-center gap-1 bg-slate-100 rounded-lg p-1 w-fit mx-auto">
              {CYCLES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`cycle-${c.value}`}
                  onClick={() => setCycle(c.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    cycle === c.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {c.label}
                  {c.value === "yearly" && (
                    <span className="inline-flex items-center px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold" data-testid="yearly-savings-badge">
                      2 Monate geschenkt
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Plan Cards */}
            {loadingCatalog ? (
              <Card className="p-8 text-center text-slate-500">Pakete werden geladen …</Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {catalog.map((p) => (
                  <PlanCard
                    key={p.plan_name}
                    plan={p}
                    cycle={cycle}
                    selected={planName === p.plan_name}
                    onSelect={setPlanName}
                  />
                ))}
              </div>
            )}

            {/* Volumen-Hinweis nur im Wholesale-Modus ab 5 aktiven Mandanten */}
            {pricingMode === "wholesale" && volumeHint?.show_custom_deal_cta && (
              <Card className="p-3 border-orange-200 bg-orange-50/60 flex items-center justify-between gap-3 flex-wrap" data-testid="volume-hint-card">
                <div className="flex items-start gap-2.5">
                  <Sparkles className="w-4 h-4 text-orange-700 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-orange-900">
                    Sie betreuen bereits <strong>{volumeHint.active_count}</strong> aktive Mandanten. Für individuelle Konditionen melden Sie sich bei uns.
                  </div>
                </div>
                <a
                  data-testid="volume-hint-cta"
                  href="mailto:sales@revenioo.ai?subject=Individueller%20Agentur-Deal%20anfragen"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold"
                >
                  <Mail className="w-3.5 h-3.5" /> Individuellen Agentur-Deal anfragen
                </a>
              </Card>
            )}

            <p className="text-[11px] text-slate-400 text-center">
              Preise zzgl. gesetzlicher USt., sofern anwendbar. Kein Trial, keine automatischen Up- oder Downgrades.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-form">
            <div className="bg-orange-50/60 border border-orange-200 rounded-lg p-3 text-xs flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-medium text-slate-900">{selectedPlan?.plan_name?.replace(/^Client /, "")}</p>
                <p className="text-slate-500">
                  {cycle === "yearly"
                    ? `Jährlich · ${selectedPlan ? formatEur(Math.round(selectedPlan.yearly_price / 12)) : "–"} /Monat (${selectedPlan ? formatEur(selectedPlan.yearly_price) : "–"} jährlich abgerechnet)`
                    : `Monatlich · ${selectedPlan ? formatEur(selectedPlan.monthly_price) : "–"} /Monat`}
                </p>
              </div>
              <Button variant="ghost" size="sm" data-testid="change-plan-btn" onClick={() => setStep(1)} className="text-xs h-8">
                <ArrowLeft className="w-3 h-3 mr-1" /> Paket ändern
              </Button>
            </div>

            <div>
              <Label className="text-xs">Firmenname des Mandanten *</Label>
              <Input
                data-testid="wizard-company-name"
                value={form.company_name}
                onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                placeholder="Solar Sonnenkraft GmbH"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ansprechpartner</Label>
                <Input
                  data-testid="wizard-contact-person"
                  value={form.contact_person}
                  onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))}
                  placeholder="Max Mustermann"
                />
              </div>
              <div>
                <Label className="text-xs">E-Mail *</Label>
                <Input
                  data-testid="wizard-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="kontakt@example.de"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Telefon (optional)</Label>
              <Input
                data-testid="wizard-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+49 …"
              />
            </div>

            <p className="text-[11px] text-slate-400">
              Nach erfolgreicher Zahlung erstellen wir den Mandanten automatisch. Es wird derzeit
              <strong> keine automatische E-Mail</strong> an den Mandanten versendet.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="outline"
              onClick={goBack}
              disabled={submitting}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Abbrechen
              </Button>
              <Button
                data-testid="wizard-next-btn"
                onClick={goNext}
                disabled={!planName || loadingCatalog}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Weiter <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <Button
              data-testid="wizard-checkout-btn"
              onClick={onSubmit}
              disabled={submitting || !validForm}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Stripe-Checkout öffnen …</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> Mandantenpaket kaufen</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Platform-Admin-Only: Mode-Switch zur Vorschau Direct vs. Wholesale
function PlatformAdminModeSwitch({ modeOverride, setModeOverride, currentMode }) {
  const { user } = useAuth();
  if (!user || user.role !== "platform_admin") return null;
  const opts = [
    { v: "", label: `Standard (${currentMode === "wholesale" ? "Wholesale" : "Direct"})` },
    { v: "direct", label: "Direct erzwingen" },
    { v: "wholesale", label: "Wholesale erzwingen" },
  ];
  return (
    <div className="flex items-center justify-center gap-1 text-[11px]" data-testid="platform-admin-mode-switch">
      <span className="text-slate-500 mr-1">Platform-Admin · Modus:</span>
      <div className="inline-flex bg-slate-100 rounded-md p-0.5">
        {opts.map((o) => (
          <button
            key={o.v || "default"}
            type="button"
            data-testid={`admin-mode-${o.v || "default"}`}
            onClick={() => setModeOverride(o.v)}
            className={`px-2 py-1 rounded text-[11px] font-medium ${modeOverride === o.v ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
